import { Locales, QuestionType, getField } from "@simplyk/common";

import { NewsletterFormQuestion, NewsletterFormSignupAnswer } from "../types";

// Utm questions are filled by the platform, never asked. The rendered order and the answer defaults
// both go through here so a field's index always matches its entry in the answers array.
export const getAnswerableQuestions = (questions: NewsletterFormQuestion[]): NewsletterFormQuestion[] =>
  questions
    .filter((question) => question.type !== QuestionType.Utm)
    .sort((left, right) => left.sortIndex - right.sortIndex);

export const getQuestionLabel = (question: NewsletterFormQuestion, locale: Locales): string =>
  getField(question.questionFields ?? [], locale)?.body ?? "";

export const getQuestionChoices = (
  question: NewsletterFormQuestion,
  locale: Locales
): Array<{ id: string; body: string }> =>
  (question.choices ?? []).map((choice) => ({
    id: choice.id,
    body: getField(choice.choiceFields ?? [], locale)?.body ?? "",
  }));

// choiceIds starts empty so the checkbox group renders from form state instead of its own
export const buildAnswerDefaultValues = (questions: NewsletterFormQuestion[]): NewsletterFormSignupAnswer[] =>
  getAnswerableQuestions(questions).map((question) => ({ questionId: question.id, choiceIds: [] }));

// The server takes one flat answer per question, so the type-specific fields collapse here. Date needs
// no case: the picker already writes the yyyy-MM-dd string the server stores.
export const toSignupAnswersPayload = (
  answers: NewsletterFormSignupAnswer[] | undefined,
  questions: NewsletterFormQuestion[]
): Array<{ questionId: string; answer?: string; choiceIds: string[] }> => {
  const typeByQuestionId = new Map(questions.map((question) => [question.id, question.type]));

  return (answers ?? [])
    .filter((answer) => typeByQuestionId.has(answer.questionId))
    .map((answer) => {
      const { questionId } = answer;

      switch (typeByQuestionId.get(questionId)) {
        case QuestionType.MultipleCheckboxes:
          return { questionId, choiceIds: answer.choiceIds ?? [] };
        case QuestionType.Multiple:
          return { questionId, choiceIds: answer.choiceId ? [answer.choiceId] : [] };
        case QuestionType.Checkbox:
          return { questionId, answer: answer.isChecked ? "true" : "false", choiceIds: [] };
        // An untouched date picker leaves null behind, which the input schema rejects as a non-string
        default:
          return { questionId, answer: answer.answer ?? undefined, choiceIds: [] };
      }
    });
};
